import React from "react";
import { Link, useParams } from "react-router-dom";
import { ShoppingBag, WandSparkles, Utensils } from "lucide-react";
import SiteShell from "../../components/layout/SiteShell";
import SEO from "../../components/seo/SEO";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";
import ConversionCtas from "../../components/content/ConversionCtas";
import IngredientLibraryContent from "../../components/ingredients/IngredientLibraryContent";
import IngredientCommunityNotes from "../../components/ingredients/IngredientCommunityNotes";
import { getIngredientBySlug, INGREDIENT_IMAGE_FALLBACK } from "../../lib/ingredients";
import { getBlendsUsingIngredient } from "../../lib/blends";

const navLink = "text-xs md:text-sm text-forest/90 hover:text-forest whitespace-nowrap border-b border-transparent hover:border-gold/60 pb-0.5 transition";

export default function IngredientDetailPage() {
  const { slug } = useParams();
  const ing = slug ? getIngredientBySlug(slug) : null;
  const related = ing ? getBlendsUsingIngredient(ing.id) : [];

  if (!ing) {
    return (
      <SiteShell theme="dark">
        <SEO title="Ingredient not found" description="This Moss Matrix ingredient page could not be found." path="/ingredients" />
        <ContentPageLayout eyebrow="Ingredients" title="Not found" subtitle="That ingredient page does not exist.">
          <Link to="/ingredients" className="underline text-forest">
            Back to library
          </Link>
        </ContentPageLayout>
      </SiteShell>
    );
  }

  const path = `/ingredients/${ing.slug}`;
  const imgAlt = ing.imageAlt || `${ing.name} ingredient image`;
  const L = ing.library;
  const pairChips = (L?.pairings || []).slice(0, 6);

  const faqJsonLd =
    L?.faqs?.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: L.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  return (
    <SiteShell theme="dark">
      <SEO
        title={ing.seoTitle}
        description={ing.seoDescription}
        path={path}
        ogType="article"
        jsonLd={faqJsonLd ? [faqJsonLd] : []}
      />
      <main className="pt-8 md:pt-12 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="rounded-2xl border border-amber-200/50 bg-[#fdf8f0] px-4 py-3 text-xs text-botanical/85 leading-relaxed max-w-4xl mb-6">
            <strong className="text-forest font-medium">Food and routine education only.</strong> This page is not medical advice. If you are pregnant,
            breastfeeding, taking medication, managing a health condition, buying for a child, or unsure, speak with a healthcare professional before using
            concentrated herbs or sea botanicals.
          </div>

          <nav
            className="sticky top-0 z-30 -mx-6 px-6 md:static md:mx-0 md:px-0 mb-8 md:mb-10 border-b border-cream/90 bg-offwhite/95 md:bg-transparent md:border-0 backdrop-blur-sm py-3 md:py-0"
            aria-label="On this page"
          >
            <div className="flex flex-wrap gap-x-4 gap-y-2 md:gap-x-6 max-w-4xl">
              <a href="#ingredient-hero" className={navLink}>
                Overview
              </a>
              <a href="#library-tradition" className={navLink}>
                Tradition
              </a>
              <a href="#library-taste" className={navLink}>
                Taste
              </a>
              <a href="#library-pairings" className={navLink}>
                Pairings
              </a>
              <a href="#library-cautions" className={navLink}>
                Cautions
              </a>
              <a href="#library-faq" className={navLink}>
                FAQ
              </a>
              <a href="#community-notes" className={navLink}>
                Community notes
              </a>
            </div>
          </nav>

          <div id="ingredient-hero" className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start scroll-mt-28 mb-10">
            <div className="rounded-[2rem] overflow-hidden border border-cream aspect-[4/5] max-h-[420px] bg-cream">
              <img src={ing.image || INGREDIENT_IMAGE_FALLBACK} alt={imgAlt} className="w-full h-full object-cover editorial-img" />
            </div>
            <div className="space-y-5 text-sm text-botanical/85 leading-relaxed">
              <div>
                <span className="inline-block text-xs tracking-[0.25em] uppercase text-botanical/70">{ing.category}</span>
                <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-forest mt-2 leading-tight">{ing.name}</h1>
                <p className="mt-3 text-base text-botanical/85">{ing.shortDescription}</p>
              </div>
              <div className="rounded-2xl border border-cream bg-white p-5">
                <h2 className="font-serif text-lg text-forest flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
                  <span>Flavour profile</span>
                </h2>
                <p className="mt-2">
                  <strong className="text-forest">Taste profile:</strong> {ing.flavour}
                </p>
                <p className="mt-2">
                  <strong className="text-forest">Colour hint:</strong> {ing.colourHint}
                </p>
              </div>
              {pairChips.length > 0 ? (
                <div>
                  <p className="text-xs uppercase tracking-widest text-botanical/60 mb-2">Pairing ideas</p>
                  <div className="flex flex-wrap gap-2">
                    {pairChips.map((p) => (
                      <span key={p.label} className="rounded-full border border-gold/30 bg-cream/50 px-3 py-1 text-[11px] text-forest">
                        {p.label}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-3 pt-1">
                {ing.blendAvailable ? (
                  <Link
                    to="/mix-ins"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-sm font-medium hover:bg-botanical transition shadow-soft"
                  >
                    <WandSparkles className="w-4 h-4 text-gold shrink-0" strokeWidth={1.5} aria-hidden />
                    Explore mix-in ideas
                  </Link>
                ) : (
                  <>
                    <a
                      href="#library-tradition"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/20 text-forest px-6 py-3 text-sm font-medium hover:bg-cream transition"
                    >
                      Learn about this ingredient
                    </a>
                    <Link
                      to="/contact?topic=suggest-botanical"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/20 text-forest px-6 py-3 text-sm font-medium hover:bg-cream transition"
                    >
                      Suggest for future mixes
                    </Link>
                  </>
                )}
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/20 text-forest px-6 py-3 text-sm font-medium hover:bg-cream transition"
                >
                  <ShoppingBag className="w-4 h-4 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
                  Shop sea moss
                </Link>
              </div>
              {ing.blendAvailable ? (
                <p className="rounded-2xl border border-forest/15 bg-forest/5 px-4 py-3 text-xs text-forest">
                  <strong className="text-forest font-medium">Common pairing idea.</strong> Custom blends are being simplified — start with
                  plain Gold or Purple. Selected mix-ins may return later.
                </p>
              ) : (
                <p className="rounded-2xl border border-cream bg-offwhite px-4 py-3 text-xs text-botanical/85">
                  <strong className="text-forest font-medium">Learn only.</strong> This page is for taste and routine reading. It is not a
                  checkout add-on right now.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-gold/30 bg-cream/50 px-5 py-4 text-xs text-botanical/85 leading-relaxed max-w-3xl mb-10">
            <strong className="text-forest font-medium">How people use herbs:</strong> Many customers stir finished sea moss gel into shakes,
            oats, yoghurt, coconut water, juice, or warm drinks at home. Moss Matrix focuses on plain Gold and Purple first while custom
            blends are being simplified.
          </div>

          <IngredientLibraryContent ing={ing} />

          {related.length > 0 && (
            <section className="rounded-3xl border border-cream bg-white p-8 mb-12">
              <h2 className="font-serif text-2xl text-forest">Related blend routines</h2>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                {related.map((b) => (
                  <li key={b.slug}>
                    <Link
                      to={`/blends/${b.slug}`}
                      className="block rounded-2xl border border-cream bg-offwhite px-4 py-3 hover:border-gold/50 transition"
                    >
                      <span className="font-serif text-forest">{b.name}</span>
                      <span className="block text-xs text-botanical/70 mt-1 line-clamp-2">{b.headline}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <IngredientCommunityNotes ingredientId={ing.id} ingredientName={ing.name} />

          <WellnessDisclaimer className="mb-10" />
          <ConversionCtas />
          <p className="text-center text-sm text-botanical/70 mt-10">
            <Link to="/ingredients" className="underline hover:text-forest">
              All ingredients
            </Link>
            {" · "}
            <Link to="/blends" className="underline hover:text-forest">
              Blend routines
            </Link>
          </p>
        </div>
      </main>
    </SiteShell>
  );
}
